import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Crown } from "lucide-react";
import { BRAND } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services & Prayer" },
  { to: "/booking", label: "Booking" },
  { to: "/blueprint", label: "Blueprint & Store" },
  { to: "/give", label: "Give" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0000FF]/85 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(11,18,48,0.18)] border-b border-white/10" : "bg-[#0000FF]/40 backdrop-blur-md"
      }`}
    >
      <div className="container-narrow flex items-center justify-between py-3">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-4 group">
          <img
            src={BRAND.logoChurch}
            alt="The Surrender Church crest"
            className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover ring-2 ring-[#D4AF37]/60 group-hover:ring-[#D4AF37] transition shadow-lg"
          />
          <div className="leading-tight">
            <div className="font-serif text-xl sm:text-2xl text-white tracking-tight">
              The <span className="gold-text">Surrender</span> Church
            </div>
            <div className="text-[10px] sm:text-[11px] tracking-[0.35em] text-[#ECC23D] uppercase font-semibold mt-1">
              Abundant Life II
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide transition relative ${
                  isActive ? "text-[#ECC23D]" : "text-slate-200 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/booking"
            data-testid="navbar-book-cta"
            className="hidden md:inline-flex btn-gold !py-2.5 !px-5 text-sm"
          >
            <Crown className="w-4 h-4" /> Book Pastor
          </Link>
          {user?.is_admin && (
            <Link
              to="/dashboard"
              data-testid="navbar-admin-link"
              className="hidden md:inline-flex text-xs px-3 py-2 rounded-full border border-[#D91668]/50 text-[#D91668] hover:bg-[#D91668]/10"
            >
              Admin
            </Link>
          )}
          <button
            data-testid="navbar-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-md text-white hover:text-[#ECC23D]"
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0000FF]/95 backdrop-blur-xl border-t border-white/10 shadow-xl">
          <div className="container-narrow py-6 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                className={({ isActive }) =>
                  `py-3 px-2 rounded-lg text-base font-semibold ${isActive ? "text-[#ECC23D] bg-white/5" : "text-white hover:bg-white/5"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/booking" onClick={()=>setOpen(false)} className="btn-gold mt-3">
              <Crown className="w-4 h-4" /> Book Pastor
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
